import Moment from 'moment';
import * as Ramda from 'ramda';

import { determineIfAllowedScopes } from '../../../../../utils/next/auth.utils';
import { _isOneOf } from '../../../../../utils/data.utils';
import { isP100OpenForRenewalQuote } from '../../../../../utils/p100GraceWindow.utils';

import { ProductTypes } from '../../../../../types';
import {
  EXPIRED_STATUS,
  IN_FORCE_STATUS,
  ISSUED_STATUS,
  RENEWED_STATUS,
  CANCELLED_STATUS,
  CANCELLED_IN_REINSTATEMENT_PERIOD,
  CANCELLED_PENDING_STATUS,
  FLAT_CANCELLED_STATUS,
} from '../../../../../console/_statics/policy.statics';

import PoliciesService from '../../../../../console/policies/policies.service';
import { VOID_STATUS } from '../../../../../console/_statics/quote.statics';

import type { PolicyTableInfoDto } from '../../../../../types/policy/tables/policy-table-info.dto';
import type { Permissions } from '../../../../auth/permissions';

interface IOptions {
  flags: {
    isFlatCancelFeatureOn: boolean;
    isUndoCancelledPolicyFeatureOn: boolean;
    renewalsFeature: boolean;
    isPrimePlusRenewalMigrationOn: boolean;
  };
}

export const getPolicyActionsForCowbell = (
  policy: PolicyTableInfoDto,
  scopes: Permissions[],
  options: IOptions
) => {
  const {
    isFlatCancelFeatureOn,
    isUndoCancelledPolicyFeatureOn,
    renewalsFeature,
    isPrimePlusRenewalMigrationOn,
  } = options.flags;

  const hasActiveTailCoverage =
    Boolean(policy.hasTailCoverage) || Boolean(policy.isErp);
  const isBorOfferedByStatus =
    PoliciesService.isActivePolicyByStatus(policy) ||
    (PoliciesService.isCancelledOrPendingCancellation(policy) &&
      hasActiveTailCoverage) ||
    policy.status === EXPIRED_STATUS;

  const CAN_DOWNLOAD_CANCELLATION_NOTICE =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    policy.status === CANCELLED_STATUS &&
    PoliciesService.hasCancellationNotice(policy);

  const CAN_DOWNLOAD_LOSS_RUNS =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    Boolean(policy.isLossRunsAvailable) &&
    policy.product !== ProductTypes.prime_tech_plus;

  const CAN_CHANGE_BOR = (() => {
    const hasProperScopes = determineIfAllowedScopes(scopes, [
      'account:cowbell',
    ]);
    const isNotRescindable = !PoliciesService.isRescindable(policy);
    const isNotEndorsementOrIsRaterXCompatible =
      !policy.isEndorsement || policy.raterXCompatible;

    return (
      hasProperScopes &&
      isBorOfferedByStatus &&
      isNotRescindable &&
      isNotEndorsementOrIsRaterXCompatible
    );
  })();

  const CAN_CHANGE_BILLING = (function IIFE() {
    const today = Moment().startOf('day');
    const effectiveDate = Moment.utc(policy.effectiveDate);

    const isEffectiveDateGreaterThanThirtyDaysAgo =
      today.diff(effectiveDate, 'days') > 30;

    return (
      ![ProductTypes.prime_tech_plus].some(Ramda.equals(policy.product)) &&
      !policy.isEndorsement &&
      PoliciesService.isActivePolicyByStatus(policy) &&
      PoliciesService.isPayable(policy) &&
      (!isEffectiveDateGreaterThanThirtyDaysAgo || !policy.isSurplus)
    );
  })();

  const CAN_GENERATE_LOSS_RUNS =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    !policy.isLossRunsAvailable &&
    ![CANCELLED_STATUS, VOID_STATUS].includes(policy.status) &&
    ![ProductTypes.prime_tech_plus].some(Ramda.equals(policy.product));

  // Prime plus is excluded by default, but legacy prime plus policies renew onto PRIME_ONE_PLUS_US,
  // so once the migration flag is on they must be opt-out-able like any other renewable policy.
  const isPrimePlusExcludedFromOptOut =
    policy.isPrimePlus && !isPrimePlusRenewalMigrationOn;

  const CAN_OPTOUT_RENEWAL_PROCESS =
    determineIfAllowedScopes(scopes, ['policy:view', 'account:cowbell']) &&
    PoliciesService.isActivePolicyByStatus(policy) &&
    !policy.isEndorsement &&
    !isPrimePlusExcludedFromOptOut &&
    policy.product !== ProductTypes.prime_tech_plus &&
    policy.product !== ProductTypes.p_one_plus &&
    policy.product !== ProductTypes.prime_one_tech_plus;

  const CAN_APPLY_PAYMENT =
    determineIfAllowedScopes(scopes, ['policy:apply_payment']) &&
    !PoliciesService.isNonPremiumBearingEndorsement(policy);

  const CAN_ISSUE_REFUND =
    determineIfAllowedScopes(scopes, [
      'account:cowbell',
      'account_profile:manage',
    ]) &&
    (PoliciesService.isActivePolicyByStatus(policy) ||
      PoliciesService.isCancelledPolicyByStatus(policy)) &&
    !PoliciesService.isNonPremiumBearingEndorsement(policy);

  const CAN_RESCIND_P100_POLICY = (() => {
    const hasUserPermission = determineIfAllowedScopes(scopes, [
      'account:cowbell',
    ]);

    return (
      hasUserPermission &&
      policy.status !== CANCELLED_STATUS &&
      policy.isNocSent &&
      PoliciesService.isPolicyCancellationDateTodayOrInTheFuture(policy) &&
      policy.product === ProductTypes.p100 &&
      !policy.isEndorsement
    );
  })();

  const CAN_RESCIND_P250_POLICY = (() => {
    const hasUserPermission = determineIfAllowedScopes(scopes, [
      'account:cowbell',
    ]);

    return (
      hasUserPermission &&
      policy.status !== CANCELLED_STATUS &&
      policy.isNocSent &&
      PoliciesService.isPolicyCancellationDateTodayOrInTheFuture(policy) &&
      policy.product === ProductTypes.p250 &&
      !policy.isEndorsement
    );
  })();

  const CAN_REINSTATE_P100_POLICY = (() => {
    const hasUserPermission = determineIfAllowedScopes(scopes, [
      'account:cowbell',
    ]);

    return (
      hasUserPermission &&
      policy.status === CANCELLED_IN_REINSTATEMENT_PERIOD &&
      policy.product === ProductTypes.p100 &&
      !policy.isEndorsement
    );
  })();

  const CAN_REINSTATE_P250_POLICY = (() => {
    const hasUserPermission = determineIfAllowedScopes(scopes, [
      'account:cowbell',
    ]);

    return (
      hasUserPermission &&
      policy.status === CANCELLED_IN_REINSTATEMENT_PERIOD &&
      policy.product === ProductTypes.p250 &&
      !policy.isEndorsement
    );
  })();

  const CAN_ADD_PREMIUM_FINANCER =
    determineIfAllowedScopes(scopes, [
      'account:cowbell',
      'account_profile:manage',
    ]) &&
    (PoliciesService.isActivePolicyByStatus(policy) ||
      (PoliciesService.isCancelledPolicyByStatus(policy) &&
        !policy.firmographicData?.agencyBilled)) &&
    PoliciesService.isPayable(policy) &&
    !policy.isPremiumFinanced &&
    ![
      ProductTypes.prime_tech_plus,
      ProductTypes.p_one_plus,
      ProductTypes.prime_one_tech_plus,
      ProductTypes.zsp,
      ProductTypes.prime_one_au,
    ].some(Ramda.equals(policy.product));

  const CAN_CHARGE_BANK_ACCOUNT =
    determineIfAllowedScopes(scopes, ['account:cowbell', 'role:admin']) &&
    (PoliciesService.isActivePolicyByStatus(policy) ||
      PoliciesService.isCancelledPolicyByStatus(policy)) &&
    !PoliciesService.isNonPremiumBearingEndorsement(policy);

  const CAN_CHARGE_CREDIT_CARD =
    determineIfAllowedScopes(scopes, ['account:cowbell', 'role:admin']) &&
    (PoliciesService.isActivePolicyByStatus(policy) ||
      PoliciesService.isCancelledPolicyByStatus(policy)) &&
    !PoliciesService.isNonPremiumBearingEndorsement(policy);

  const CAN_MARK_CHECK_IN_MAIL =
    determineIfAllowedScopes(scopes, ['account:cowbell', 'role:admin']) &&
    (PoliciesService.isActivePolicyByStatus(policy) ||
      PoliciesService.isCancelledPolicyByStatus(policy));

  const CAN_CANCEL_POLICY =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    policy.status === IN_FORCE_STATUS &&
    !policy.isErp &&
    !policy.isEndorsement &&
    ![
      ProductTypes.prime_tech_plus,
      ProductTypes.p_one_plus,
      ProductTypes.prime_one_tech_plus,
    ].some(Ramda.equals(policy.product));

  const CAN_UPDATE_POLICY =
    determineIfAllowedScopes(scopes, ['account:cowbell', 'root:root'], true) &&
    PoliciesService.isActivePolicyByStatus(policy) &&
    policy.product === ProductTypes.p250;

  const CAN_UPDATE_COMMISSION =
    determineIfAllowedScopes(scopes, ['account:cowbell', 'accounts:uw']) &&
    PoliciesService.isActivePolicyByStatus(policy) &&
    _isOneOf(policy.status, [ISSUED_STATUS, IN_FORCE_STATUS]) &&
    ![
      ProductTypes.p_one_plus,
      ProductTypes.prime_one,
      ProductTypes.prime_one_tech_plus,
    ].some(Ramda.equals(policy.product));

  const CAN_UPDATE_POLICY_DATES =
    determineIfAllowedScopes(scopes, ['account:cowbell', 'root:root'], true) &&
    PoliciesService.isActivePolicyByStatus(policy) &&
    policy.product === ProductTypes.p250;

  const CAN_SEND_NOTICE_OF_CANCELLATION =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    !policy.isEndorsement &&
    policy.status === IN_FORCE_STATUS;

  const CAN_FLAT_CANCEL_POLICY =
    isFlatCancelFeatureOn &&
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    PoliciesService.isActivePolicyByStatus(policy) &&
    Moment.utc().isSameOrAfter(Moment(policy.effectiveDate)) &&
    policy.product === ProductTypes.p250 &&
    !policy.isEndorsement;

  const CAN_VOID_POLICY =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    ((policy.product === ProductTypes.prime_tech_plus &&
      PoliciesService.isActivePolicyByStatus(policy)) ||
      (PoliciesService.isVoidable(policy) &&
        !(policy.isEndorsement && policy.product === ProductTypes.p250)));

  const CAN_VIEW_RATER =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    determineIfAllowedScopes(scopes, ['policy:view']) &&
    (PoliciesService.isActivePolicyByStatus(policy) ||
      [RENEWED_STATUS, CANCELLED_STATUS, EXPIRED_STATUS, VOID_STATUS].includes(
        policy.status
      )) &&
    policy.product === ProductTypes.p250 &&
    !policy.isPrimePlus;

  const CAN_HANDLE_PAYMENT_REMINDERS =
    determineIfAllowedScopes(scopes, ['account:cowbell'], true) &&
    PoliciesService.isActivePolicyByStatus(policy) &&
    PoliciesService.isPayable(policy) &&
    policy.product !== ProductTypes.prime_tech_plus;

  // Expediting and rescinding a BOR change share the same eligibility rule.
  const isEligibleForBorStatusChange = (() => {
    const hasProperScopes = determineIfAllowedScopes(scopes, [
      'account:cowbell',
    ]);
    const isRescindable = PoliciesService.isRescindable(policy);
    const isNotEndorsementOrIsRaterXCompatible =
      !policy.isEndorsement || policy.raterXCompatible;
    const isNotRenewed = policy.status !== RENEWED_STATUS;

    return (
      hasProperScopes &&
      isBorOfferedByStatus &&
      isRescindable &&
      isNotEndorsementOrIsRaterXCompatible &&
      isNotRenewed
    );
  })();

  const CAN_EXPEDITE_BOR = isEligibleForBorStatusChange;
  const CAN_RESCIND_BOR = isEligibleForBorStatusChange;

  const CAN_REGENERATE_INVOICE =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    PoliciesService.isActivePolicyByStatus(policy) &&
    !PoliciesService.isNonPremiumBearingEndorsement(policy) &&
    policy.product !== ProductTypes.prime_tech_plus;

  const CAN_REGENERATE_POLICY_DOCUMENTS =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    PoliciesService.isActivePolicyByStatus(policy) &&
    ![
      ProductTypes.prime_tech_plus,
      ProductTypes.p_one_plus,
      ProductTypes.prime_one_tech_plus,
    ].some(Ramda.equals(policy.product));

  const CAN_UPDATE_AGENT_INFO =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    (PoliciesService.isActivePolicyByStatus(policy) ||
      policy.status === 'EXPIRED' ||
      policy.status === 'CANCELLED') &&
    ![ProductTypes.prime_tech_plus].some(Ramda.equals(policy.product));

  const CAN_EXTEND_PAYMENT_DUE_DATE =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    policy.product === ProductTypes.p250 &&
    !policy.isNocSent &&
    policy.status === IN_FORCE_STATUS;

  const CAN_EXTEND_CANCELLATION_DATE =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    policy.isNocSent &&
    policy.status === IN_FORCE_STATUS;

  const CAN_START_NOC_PROCESS =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    !policy.isNocSent &&
    policy.status === IN_FORCE_STATUS &&
    !policy.isPrimePlus;

  const CAN_STOP_NOC_PROCESS =
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    policy.isNocSent &&
    policy.status === CANCELLED_PENDING_STATUS;

  const CAN_VIEW_NOC_OPTIONS =
    !policy.isEndorsement &&
    !policy.isPaid &&
    policy.product !== ProductTypes.prime_tech_plus &&
    policy.product !== ProductTypes.p_one_plus &&
    policy.product !== ProductTypes.prime_one_tech_plus &&
    (CAN_EXTEND_PAYMENT_DUE_DATE ||
      CAN_EXTEND_CANCELLATION_DATE ||
      CAN_START_NOC_PROCESS ||
      CAN_STOP_NOC_PROCESS);

  const CAN_DELETE_POLICY =
    determineIfAllowedScopes(
      scopes,
      ['account:cowbell', 'policy:manage'],
      true
    ) &&
    !PoliciesService.isActivePolicyByStatus(policy) &&
    policy.status !== RENEWED_STATUS;

  const CAN_DOWNLOAD_APPLICATION =
    determineIfAllowedScopes(scopes, ['policy:view']) &&
    (PoliciesService.isActivePolicyByStatus(policy) ||
      policy.status === RENEWED_STATUS) &&
    !policy.isPrimePlus &&
    policy.product !== ProductTypes.prime_tech_plus;

  const CAN_DOWNLOAD_FINANCING_AGREEMENT =
    determineIfAllowedScopes(scopes, ['policy:view']) &&
    (PoliciesService.isActivePolicyByStatus(policy) ||
      policy.status === RENEWED_STATUS) &&
    policy.isPremiumFinanced &&
    policy.isDocumentAvailable;

  const CAN_DOWNLOAD_INVOICE =
    determineIfAllowedScopes(scopes, ['policy:view']) &&
    (policy.isInvoiceAvailable || policy.isDocumentAvailable) &&
    !PoliciesService.isNonPremiumBearingEndorsement(policy);

  const CAN_DOWNLOAD_POLICY_DOCUMENTS =
    determineIfAllowedScopes(scopes, ['policy:view']) &&
    policy.isDocumentAvailable &&
    (PoliciesService.isActivePolicyByStatus(policy) ||
      policy.status === RENEWED_STATUS);

  const CAN_DOWNLOAD_RECEIPT =
    determineIfAllowedScopes(scopes, ['policy:view']) &&
    policy.isDocumentAvailable &&
    policy.isPaid &&
    (PoliciesService.isActivePolicyByStatus(policy) ||
      policy.status === RENEWED_STATUS);

  const CAN_GENERATE_POLICY_CERTIFICATE =
    determineIfAllowedScopes(scopes, ['policy:view']) &&
    policy.isCertificateAvailable &&
    (PoliciesService.isActivePolicyByStatus(policy) ||
      policy.status === RENEWED_STATUS) &&
    policy.product !== ProductTypes.prime_tech_plus;

  const CAN_GENERATE_ERP =
    policy.product === ProductTypes.p250 &&
    (policy.status === CANCELLED_STATUS || policy.status === EXPIRED_STATUS) &&
    !policy.isEndorsement;

  const CAN_VOID_ERP = policy.status === IN_FORCE_STATUS && policy.isErp;

  const CAN_GENERATE_RENEWAL_SUBMISSION =
    determineIfAllowedScopes(scopes, ['renewals:manage']) &&
    policy.status === IN_FORCE_STATUS &&
    !policy.isPrimePlus &&
    !policy.isEndorsement &&
    policy.product !== ProductTypes.prime_tech_plus;

  // Legacy Prime Plus (P250 + isPrimePlus) excess policies migrate to PRIME_ONE_PLUS_US on renewal.
  // Their submission is created via the account renewal-submission endpoint (like Prime X), distinct
  // from the legacy product-typed CAN_GENERATE_RENEWAL_SUBMISSION path, and is gated on the migration
  // feature flag so it lights up in lockstep with the backend switch.
  //
  // EXPIRED is allowed alongside IN_FORCE: legacy Prime Plus policies were never picked up by the
  // renewal scheduler before the migration, so the back book that needs renewing has already lapsed.
  // The backend endpoint has no status check of its own, so this is the only gate.
  const CAN_CREATE_PRIME_PLUS_RENEWAL_SUBMISSION =
    isPrimePlusRenewalMigrationOn &&
    Boolean(policy.isPrimePlus) &&
    determineIfAllowedScopes(scopes, ['renewals:manage']) &&
    [IN_FORCE_STATUS, EXPIRED_STATUS].includes(policy.status) &&
    !policy.isEndorsement;

  const CAN_MUTE_RENEWAL_REMINDER = (function IIFE() {
    const today = Moment.utc().startOf('D');
    const endDate = Moment.utc(policy.endDate);
    const daysFromRenewal = Moment.duration(endDate.diff(today)).asDays();

    const isP250OpenForEarlyRenewal =
      policy.product === ProductTypes.p250 &&
      daysFromRenewal <= 120 &&
      daysFromRenewal >= 0;

    const isP100OpenForEarlyRenewal =
      policy.product === ProductTypes.p100 && policy.isOpenForRenewal;

    return (
      determineIfAllowedScopes(scopes, ['account:cowbell']) &&
      (isP100OpenForEarlyRenewal || isP250OpenForEarlyRenewal) &&
      [IN_FORCE_STATUS, RENEWED_STATUS, EXPIRED_STATUS].includes(
        policy.status
      ) &&
      !policy.isEndorsement
    );
  })();

  const CAN_REINSTATE =
    determineIfAllowedScopes(scopes, ['account:cowbell', 'role:admin'], true) &&
    policy.product === ProductTypes.p250 &&
    !policy.isPrimePlus &&
    !policy.isEndorsement &&
    [VOID_STATUS, CANCELLED_STATUS, FLAT_CANCELLED_STATUS].includes(
      policy.status
    ) &&
    !checkIfCancelledForNonPayment(policy);

  // DA and cluster users also reach this getter (their policy tables render
  // AdminPolicyOptions) without account:cowbell, so they get the agency window.
  const CAN_GENERATE_RENEWAL_QUOTE =
    determineIfAllowedScopes(scopes, ['quote:manage']) &&
    ((policy.product === ProductTypes.p250 &&
      [IN_FORCE_STATUS, EXPIRED_STATUS].includes(policy.status)) ||
      (policy.product === ProductTypes.p100 &&
        renewalsFeature &&
        isP100OpenForRenewalQuote(
          policy,
          determineIfAllowedScopes(scopes, ['account:cowbell'])
        ))) &&
    !policy.isEndorsement &&
    !policy.isPrimePlus;

  const CAN_UNDO_CANCELLED_POLICY =
    isUndoCancelledPolicyFeatureOn &&
    determineIfAllowedScopes(scopes, ['account:cowbell']) &&
    !policy.isEndorsement &&
    ([ProductTypes.p100, ProductTypes.p250, ProductTypes.p100_pro].some(
      Ramda.equals(policy.product)
    ) ||
      policy.raterXCompatible) &&
    [
      VOID_STATUS,
      CANCELLED_STATUS,
      CANCELLED_PENDING_STATUS,
      FLAT_CANCELLED_STATUS,
      CANCELLED_IN_REINSTATEMENT_PERIOD,
    ].includes(policy.status);

  const CAN_UPDATE_BILLING_ADMIN =
    !policy.isEndorsement &&
    (policy.product === ProductTypes.p250 ||
      policy.product === ProductTypes.p100);

  const CAN_TOGGLE_POLICY_DOCS =
    determineIfAllowedScopes(scopes, ['superusers:manage']) &&
    [ISSUED_STATUS, IN_FORCE_STATUS].includes(policy.status);

  return {
    CAN_VOID_POLICY,
    CAN_DOWNLOAD_CANCELLATION_NOTICE,
    CAN_DOWNLOAD_LOSS_RUNS,
    CAN_CHANGE_BOR,
    CAN_CHANGE_BILLING,
    CAN_GENERATE_LOSS_RUNS,
    CAN_OPTOUT_RENEWAL_PROCESS,
    CAN_APPLY_PAYMENT,
    CAN_ISSUE_REFUND,
    CAN_ADD_PREMIUM_FINANCER,
    CAN_CHARGE_BANK_ACCOUNT,
    CAN_CHARGE_CREDIT_CARD,
    CAN_MARK_CHECK_IN_MAIL,
    CAN_CANCEL_POLICY,
    CAN_UPDATE_POLICY,
    CAN_UPDATE_COMMISSION,
    CAN_UPDATE_POLICY_DATES,
    CAN_SEND_NOTICE_OF_CANCELLATION,
    CAN_FLAT_CANCEL_POLICY,
    CAN_VIEW_RATER,
    CAN_HANDLE_PAYMENT_REMINDERS,
    CAN_EXPEDITE_BOR,
    CAN_RESCIND_BOR,
    CAN_REGENERATE_INVOICE,
    CAN_REGENERATE_POLICY_DOCUMENTS,
    CAN_UPDATE_AGENT_INFO,
    CAN_VIEW_NOC_OPTIONS,
    CAN_EXTEND_CANCELLATION_DATE,
    CAN_EXTEND_PAYMENT_DUE_DATE,
    CAN_START_NOC_PROCESS,
    CAN_STOP_NOC_PROCESS,
    CAN_DELETE_POLICY,
    CAN_DOWNLOAD_APPLICATION,
    CAN_DOWNLOAD_FINANCING_AGREEMENT,
    CAN_DOWNLOAD_INVOICE,
    CAN_DOWNLOAD_POLICY_DOCUMENTS,
    CAN_DOWNLOAD_RECEIPT,
    CAN_GENERATE_POLICY_CERTIFICATE,
    CAN_GENERATE_ERP,
    CAN_VOID_ERP,
    CAN_RESCIND_P100_POLICY,
    CAN_RESCIND_P250_POLICY,
    CAN_REINSTATE_P100_POLICY,
    CAN_REINSTATE_P250_POLICY,
    CAN_GENERATE_RENEWAL_SUBMISSION,
    CAN_CREATE_PRIME_PLUS_RENEWAL_SUBMISSION,
    CAN_MUTE_RENEWAL_REMINDER,
    CAN_REINSTATE,
    CAN_GENERATE_RENEWAL_QUOTE,
    CAN_UNDO_CANCELLED_POLICY,
    CAN_UPDATE_BILLING_ADMIN,
    CAN_TOGGLE_POLICY_DOCS,
  };
};

const checkIfCancelledForNonPayment = (policy: PolicyTableInfoDto) => {
  return (
    policy.status === CANCELLED_STATUS &&
    !!policy.nocProcess &&
    policy.nocProcess !== 'STOP'
  );
};
